import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowCaret, ContentCheck, ContentEdit, PersonRemove } from '@fs/zion-icon'
import { useQueryParams } from '@fs/zion-router'
import { Button, Row, IconButton, usePageWidth, LabelTooltip } from '@fs/zion-ui'
import { trackEvent } from '@fs/zion-analytics'
import { useFlaggedUser } from '../../../../../Give/utils/flaggedUserLibrary/FlaggedUserContext'
import NavButton from './NavButton'

export default function Navigation({
  disableBackButton,
  goToNextTask,
  goToPreviousTask,
  handleSave,
  handleClickNotAPerson,
  focusOnEditField,
  index,
  loading,
  sharedField,
}) {
  const [t] = useTranslation()
  const { checkForFlaggedBehavior } = useFlaggedUser()
  const { query } = useQueryParams()
  const pageWidth = usePageWidth()
  const isMobile = pageWidth({ default: true, md: false })

  const BackButton = useCallback(() => {
    // eslint-disable-next-line react/no-unstable-nested-components
    const BackNavButton = () => {
      return (
        <NavButton
          disabled={disableBackButton}
          title={t('navigation.back-button', 'Back')}
          icon={ArrowCaret}
          handleOnClick={goToPreviousTask}
          color="blue"
          emphasis="medium"
          iconDirection="backward"
          size="lg"
          loading={loading}
        />
      )
    }
    if (disableBackButton) {
      return (
        <LabelTooltip
          label={t('navigation.back-button.label.no-previous-name', 'No Previous Name')}
          placement="top"
          target={<BackNavButton />}
        />
      )
    }
    return <BackNavButton />
  }, [disableBackButton, goToPreviousTask, loading, t])

  return (
    <Row alignX="center" alignY="middle" gutters="lg" guttersY="xl">
      {index > 0 && <BackButton />}
      <NavButton
        title={t('navigation.match-button', 'Match')}
        icon={ContentCheck}
        handleOnClick={() => {
          handleSave()
          checkForFlaggedBehavior({
            actionType: 'match',
            ark: query.autoGenImageArk,
            timestamp: new Date(),
          })
          trackEvent({
            event_name: 'click_action',
            link_name: `Match`,
          })
        }}
        color="green"
        emphasis="lightHigh"
        alignY="top"
        size="lg"
        loading={loading}
      />
      <NavButton
        title={t('navigation.edit-button', 'Edit')}
        icon={ContentEdit}
        handleOnClick={() => {
          trackEvent({
            event_name: 'click_action',
            link_name: `Edit`,
          })
          focusOnEditField()
        }}
        color="red"
        emphasis="lightHigh"
        alignY="top"
        size="lg"
        loading={loading}
      />
      <NavButton
        title={t('navigation.unsure-button', 'Unsure')}
        icon={ArrowCaret}
        handleOnClick={() => {
          trackEvent({
            event_name: 'click_action',
            link_name: `Unsure`,
          })
          goToNextTask()
        }}
        color="blue"
        emphasis="lightHigh"
        iconDirection="forward"
        alignY="top"
        size="lg"
        loading={loading}
      />
      {isMobile ? (
        <IconButton
          size="lg"
          disabled={loading}
          Icon={PersonRemove}
          onClick={() => {
            handleClickNotAPerson()
          }}
          data-testid="not-a-person"
          linkName="Not A Person"
          title={t('navigation.not-a-person-button', 'Not a Person')}
        />
      ) : (
        <Button
          Icon={PersonRemove}
          onClick={() => {
            handleClickNotAPerson()
          }}
          emphasis="low"
          iconDirection="forward"
          size="sm"
          alignY="middle"
          disabled={sharedField || loading}
          description={t(
            'navigation.not-a-person-tooltip',
            'Sometimes the computer identifies text as a name but it may actually be a note from the census taker. Hint: Common text such as "Vacant", "No One Home", etc.'
          )}
        >
          {t('navigation.not-a-person-button', 'Not a Person')}
        </Button>
      )}
    </Row>
  )
}
